import { Alert, AlertDescription } from '@/components/ui/shadcn/alert';
import { Button } from '@/components/ui/shadcn/button';
import { EmptyState } from '@/components/ui/shadcn/empty-state';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type BreadcrumbItem } from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import {
    Building,
    Car,
    GanttChartSquare,
    LayoutDashboard,
    LayoutGrid,
    Loader2,
    LucideAxis3D,
    Plus,
    Share2,
    TriangleAlert,
    Users2,
    X,
} from 'lucide-react';

import CompaniesAnalytics from '@/components/features/Analytics/CompaniesAnalytics';
import ContactsAnalytics from '@/components/features/Analytics/ContactsAnalytics';
import OverviewAnalytics from '@/components/features/Analytics/OverviewAnalytics';
import ProjectsAnalytics from '@/components/features/Analytics/ProjectsAnalytics';
import ShareBoardModal from '@/components/features/Analytics/ShareBoardModal';
import TeamAnalytics from '@/components/features/Analytics/TeamAnalytics';
import WorkOrdersAnalytics from '@/components/features/Analytics/WorkOrdersAnalytics';

import CustomDashboardView from '@/components/features/Dashboards/CustomDashboardView';
import DashboardBuilder from '@/components/features/Dashboards/DashboardBuilder';
import type { DashboardListItem, ReportRegistry, UserDashboardSummary } from '@/types/dashboards';

import { PeriodFilter } from '@/components/ui/analytics/PeriodFilter';
import { AnalyticsDocsProvider } from '@/contexts/AnalyticsDocsContext';
import { useCan } from '@/lib/utils';
import { PageProps as SharedProps } from '@/types';
import type {
    AnalyticsDateRange,
    CompaniesAnalyticsData,
    ContactsAnalyticsData,
    DataPointDoc,
    EmployeePerformance,
    LeadConversionMetrics,
    OverdueWorkOrder,
    OverviewAnalyticsData,
    ProjectsAnalyticsData,
    TeamAnalyticsData,
    TopCompany,
    WorkOrdersAnalyticsData,
} from '@/types/analytics';
import React from 'react';

interface AnalyticsPageProps extends SharedProps {
    dateRange: AnalyticsDateRange;
    availableYears?: number[];
    /** The pre-defined boards this user may open. Board props below arrive only for these. */
    analyticsBoards?: string[];
    /** Data-point documentation for those boards, keyed "<board>.<dataPoint>". */
    boardDocs?: Record<string, DataPointDoc>;
    leadMetrics?: LeadConversionMetrics;
    employeePerformance?: EmployeePerformance[];
    topCompanies?: TopCompany[];
    projectStatusCounts?: Record<string, number>;
    projectFinancials?: { total_budget: number; total_costs: number };
    workOrderStatusCounts?: Record<string, number>;
    workOrderFinancials?: { total_cost: number; total_orders: number; completed_orders: number };
    overdueWorkOrders?: OverdueWorkOrder[];
    overdueWorkOrdersCount?: number;
    overviewAnalytics?: OverviewAnalyticsData;
    companiesAnalytics?: CompaniesAnalyticsData;
    projectsAnalytics?: ProjectsAnalyticsData;
    workOrdersAnalytics?: WorkOrdersAnalyticsData;
    teamAnalytics?: TeamAnalyticsData;
    contactsAnalytics?: ContactsAnalyticsData;
    analyticsWarnings?: string[];
    userDashboards?: DashboardListItem[];
    reportRegistry?: ReportRegistry;
    [key: string]: unknown;
}

export default function CrmAnalyticsDashboard() {
    const { props } = usePage<AnalyticsPageProps>();
    const {
        dateRange,
        leadMetrics,
        employeePerformance,
        topCompanies,
        projectStatusCounts,
        projectFinancials,
        workOrderStatusCounts,
        workOrderFinancials,
        overdueWorkOrders,
        overdueWorkOrdersCount,
        overviewAnalytics,
        companiesAnalytics,
        projectsAnalytics,
        workOrdersAnalytics,
        teamAnalytics,
        contactsAnalytics,
        analyticsWarnings,
    } = props;

    const { t } = useTranslations();
    const { can } = useCan();

    // Custom-dashboard builder data (empty when the user can't read any source).
    const reportRegistry: ReportRegistry = props.reportRegistry ?? { can_view_financial: false, shared_axes: [], sources: [] };
    const userDashboards: DashboardListItem[] = props.userDashboards ?? [];
    const allowedBoards: string[] = props.analyticsBoards ?? [];

    const ownedDashboards = userDashboards.filter((dashboard) => dashboard.is_owner);
    const sharedDashboards = userDashboards.filter((dashboard) => !dashboard.is_owner);

    const canCreateDashboards = can('dashboards.create');
    const canShareBoards = can('analytics.boards.share');

    const breadcrumbs: BreadcrumbItem[] = [{ title: t('analytics.crm_dashboard.breadcrumbs.analytics', 'Analytics'), href: '/analytics' }];

    const handleDashboardSaved = (dashboard: UserDashboardSummary) => {
        router.reload({ only: ['userDashboards'] });
        setSelectedModule(`dash:${dashboard.id}`);
    };

    // Pending state for filter navigations, so charts can show a loading overlay.
    const [isNavigating, setIsNavigating] = React.useState(false);

    const [warningsDismissed, setWarningsDismissed] = React.useState(false);
    const showWarnings = !warningsDismissed && (analyticsWarnings?.length ?? 0) > 0;

    // Map internal analytics method names to human-readable section labels.
    const warningLabels = React.useMemo(() => {
        const labelMap: Record<string, string> = {
            getOverviewAnalytics: t('analytics.crm_dashboard.modules.overview', 'Overview'),
            getRevenueProfitTimeline: t('analytics.crm_dashboard.warning_sections.revenue_timeline', 'Revenue & profit timeline'),
            getCompaniesAnalytics: t('analytics.crm_dashboard.modules.companies', 'Companies'),
            getContactsAnalytics: t('analytics.crm_dashboard.modules.contacts', 'Contacts'),
            getProjectsAnalytics: t('analytics.crm_dashboard.modules.projects', 'Projects'),
            getWorkOrdersAnalytics: t('analytics.crm_dashboard.modules.workorders', 'Work Orders'),
            getTeamAnalytics: t('analytics.crm_dashboard.modules.team', 'Team'),
        };
        return (analyticsWarnings ?? []).map((name) => labelMap[name] ?? name);
    }, [analyticsWarnings, t]);

    // Label/icon registry for the fixed boards. Which of them a user may actually
    // open is decided server-side (App\Support\AnalyticsBoards) and arrives as
    // `analyticsBoards` — the payload for a board they cannot open is never sent.
    const analyticsModules = [
        { value: 'overview', label: t('analytics.crm_dashboard.modules.overview', 'Overview'), icon: LucideAxis3D },
        { value: 'companies', label: t('analytics.crm_dashboard.modules.companies', 'Companies'), icon: Building },
        { value: 'contacts', label: t('analytics.crm_dashboard.modules.contacts', 'Contacts'), icon: Users2 },
        { value: 'projects', label: t('analytics.crm_dashboard.modules.projects', 'Projects'), icon: GanttChartSquare },
        { value: 'workorders', label: t('analytics.crm_dashboard.modules.workorders', 'Work Orders'), icon: Car },
        { value: 'team', label: t('analytics.crm_dashboard.modules.team', 'Team'), icon: Users2 },
    ];

    const availableModules = analyticsModules.filter((module) => allowedBoards.includes(module.value));

    // Which pre-built board the "Share board" button acts on — null while a custom
    // dashboard or the builder is showing, since those share differently.
    const [sharingBoard, setSharingBoard] = React.useState<string | null>(null);

    // Fall back through boards → own dashboards → the builder, so a user scoped to
    // a single board (or to none) never lands on a module they cannot see.
    const [selectedModule, setSelectedModule] = React.useState<string>(() => {
        const firstBoard = analyticsModules.find((module) => allowedBoards.includes(module.value));
        if (firstBoard) {
            return firstBoard.value;
        }
        return userDashboards[0] ? `dash:${userDashboards[0].id}` : 'create';
    });

    // Selecting a board the dropdown has not caught up with is normal: saving a new
    // dashboard switches to it while `router.reload({only:['userDashboards']})` is
    // still in flight. Fall back to an owned-and-manageable summary so the board
    // renders immediately with its Share/Edit buttons rather than flashing blank.
    // The optimistic flags are cosmetic — every write is re-checked by
    // DashboardVisibility on the server.
    const activeBoard = availableModules.find((module) => module.value === selectedModule);

    const activeDashboardId = selectedModule.startsWith('dash:') ? selectedModule.slice('dash:'.length) : null;
    const activeDashboard: DashboardListItem | undefined =
        activeDashboardId === null
            ? undefined
            : (userDashboards.find((dashboard) => dashboard.id === activeDashboardId) ?? {
                  id: activeDashboardId,
                  name: '',
                  is_owner: true,
                  can_manage: true,
                  owner_name: null,
              });

    // Render analytics component based on selected module
    const renderAnalyticsComponent = () => {
        // Nothing to show at all — no board permission and no dashboard of their own.
        if (availableModules.length === 0 && userDashboards.length === 0 && !canCreateDashboards) {
            return (
                <EmptyState
                    title={t('analytics.crm_dashboard.no_boards_title', 'No analytics boards available')}
                    description={t(
                        'analytics.crm_dashboard.no_boards_description',
                        'You do not have access to any analytics board yet. Ask an administrator to grant you one under Settings → System → Role permissions.',
                    )}
                />
            );
        }

        // Custom-dashboard builder (in-page, no route change).
        if (selectedModule === 'create') {
            if (reportRegistry.sources.length === 0) {
                return (
                    <EmptyState
                        title={t('dashboards.builder.no_sources_title', 'No data sources available')}
                        description={t(
                            'dashboards.builder.no_sources_description',
                            'You need read access to at least one entity to build a dashboard.',
                        )}
                    />
                );
            }
            return (
                <DashboardBuilder
                    registry={reportRegistry}
                    dateRange={dateRange}
                    onSaved={handleDashboardSaved}
                    onCancel={() => setSelectedModule(availableModules[0]?.value ?? 'create')}
                />
            );
        }

        // A saved custom dashboard — owned or shared.
        if (activeDashboard) {
            return (
                <CustomDashboardView
                    key={selectedModule}
                    summary={activeDashboard}
                    registry={reportRegistry}
                    dateRange={dateRange}
                    onUpdated={() => router.reload({ only: ['userDashboards'] })}
                />
            );
        }

        switch (selectedModule) {
            case 'overview':
                return <OverviewAnalytics leadMetrics={leadMetrics} overviewAnalytics={overviewAnalytics} />;
            case 'companies':
                return <CompaniesAnalytics topCompanies={topCompanies ?? []} companiesAnalytics={companiesAnalytics} />;
            case 'projects':
                return (
                    <ProjectsAnalytics
                        statusCounts={projectStatusCounts ?? {}}
                        financials={projectFinancials ?? { total_budget: 0, total_costs: 0 }}
                        projectsAnalytics={projectsAnalytics}
                    />
                );
            case 'contacts':
                return <ContactsAnalytics contactsAnalytics={contactsAnalytics} />;
            case 'workorders':
                return (
                    <WorkOrdersAnalytics
                        statusCounts={workOrderStatusCounts ?? {}}
                        financials={workOrderFinancials ?? { total_cost: 0, total_orders: 0, completed_orders: 0 }}
                        overdueOrders={overdueWorkOrders ?? []}
                        overdueCount={overdueWorkOrdersCount}
                        workOrdersAnalytics={workOrdersAnalytics}
                        dateRange={dateRange}
                    />
                );
            case 'team':
                return <TeamAnalytics employees={employeePerformance ?? []} teamAnalytics={teamAnalytics} />;
            default:
                return null;
        }
    };

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={t('analytics.crm_dashboard.title', 'CRM Analytics Dashboard')} />

            {/* Every KPI tile and chart looks its "(i)" documentation up from here. */}
            <AnalyticsDocsProvider docs={props.boardDocs}>
                <div className="flex h-full flex-1 flex-col gap-6 p-4">
                    {/* Header — title + dashboard/module selector */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div>
                            <h1 className="text-3xl font-bold">{t('analytics.crm_dashboard.heading', 'CRM Analytics Dashboard')}</h1>
                            <p className="text-muted-foreground max-w-2xl">
                                {t('analytics.crm_dashboard.subheading', 'Real-time insights across sales, projects, finance, and team performance.')}
                            </p>
                        </div>

                        <Select value={selectedModule} onValueChange={setSelectedModule}>
                            <SelectTrigger className="w-full sm:w-[220px]">
                                <span className="flex min-w-0 items-center gap-2">
                                    <LayoutGrid className="h-4 w-4 shrink-0" />
                                    <SelectValue placeholder={t('analytics.crm_dashboard.module_placeholder', 'Select analytics module')} />
                                </span>
                            </SelectTrigger>
                            <SelectContent>
                                {availableModules.map((module) => {
                                    const IconComponent = module.icon;
                                    return (
                                        <SelectItem key={module.value} value={module.value}>
                                            <div className="flex items-center gap-2">
                                                <IconComponent className="h-4 w-4" />
                                                {module.label}
                                            </div>
                                        </SelectItem>
                                    );
                                })}

                                {ownedDashboards.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>{t('analytics.crm_dashboard.my_dashboards', 'My dashboards')}</SelectLabel>
                                        {ownedDashboards.map((dashboard) => (
                                            <SelectItem key={dashboard.id} value={`dash:${dashboard.id}`}>
                                                <div className="flex items-center gap-2">
                                                    <LayoutDashboard className="h-4 w-4" />
                                                    {dashboard.name}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}

                                {sharedDashboards.length > 0 && (
                                    <SelectGroup>
                                        <SelectLabel>{t('analytics.crm_dashboard.shared_dashboards', 'Shared with me')}</SelectLabel>
                                        {sharedDashboards.map((dashboard) => (
                                            <SelectItem key={dashboard.id} value={`dash:${dashboard.id}`}>
                                                <div className="flex items-center gap-2">
                                                    <Users2 className="h-4 w-4" />
                                                    {dashboard.name}
                                                    {dashboard.owner_name && (
                                                        <span className="text-muted-foreground text-xs">· {dashboard.owner_name}</span>
                                                    )}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectGroup>
                                )}

                                {canCreateDashboards && (
                                    <SelectGroup>
                                        <SelectItem value="create">
                                            <div className="flex items-center gap-2">
                                                <Plus className="h-4 w-4" />
                                                {t('analytics.crm_dashboard.create_new', 'Create new…')}
                                            </div>
                                        </SelectItem>
                                    </SelectGroup>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    {showWarnings && (
                        <Alert className="bg-warning-subtle text-warning-subtle-foreground border-warning-subtle-foreground/30 pr-10">
                            <TriangleAlert />
                            <AlertDescription className="text-warning-subtle-foreground/90">
                                <span>
                                    {t(
                                        'analytics.crm_dashboard.partial_data_warning',
                                        "Some analytics widgets couldn't load fresh data. Numbers shown may be incomplete.",
                                    )}
                                </span>
                                {warningLabels.length > 0 && (
                                    <span className="font-medium">
                                        {t('analytics.crm_dashboard.warning_affected', 'Affected:')} {warningLabels.join(', ')}
                                    </span>
                                )}
                            </AlertDescription>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="absolute top-2 right-2 h-6 w-6"
                                onClick={() => setWarningsDismissed(true)}
                                aria-label={t('analytics.crm_dashboard.dismiss_warning', 'Dismiss')}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </Alert>
                    )}

                    {/*
                     * Filter toolbar — the shared period filter, plus the one action that
                     * belongs to the page rather than to a board. Adding widgets and saving
                     * live in the builder itself; on a pre-built board there is nothing to
                     * add a widget to and nothing to save.
                     *
                     * "New dashboard" is here rather than only in the module dropdown because
                     * building a board is the prerequisite for sharing one, and a dropdown
                     * item is not a discoverable place to start that.
                     */}
                    <div className="bg-card flex flex-col gap-3 rounded-xl border p-3 2xl:flex-row 2xl:items-center 2xl:justify-between">
                        <PeriodFilter dateRange={dateRange} availableYears={props.availableYears} onNavigatingChange={setIsNavigating} />
                        <div className="flex items-center gap-2 2xl:shrink-0">
                            {/* Pre-built boards are governed by a permission, so "share" here
                                grants it — see ShareBoardModal. Custom boards carry their own
                                Share button in their header instead. */}
                            {canShareBoards && activeBoard && (
                                <Button variant="outline" onClick={() => setSharingBoard(activeBoard.value)}>
                                    <Share2 className="mr-2 h-4 w-4" />
                                    {t('analytics.board_sharing.trigger', 'Share board')}
                                </Button>
                            )}
                            {canCreateDashboards && selectedModule !== 'create' && (
                                <Button variant="outline" onClick={() => setSelectedModule('create')}>
                                    <Plus className="mr-2 h-4 w-4" />
                                    {t('analytics.crm_dashboard.new_dashboard', 'New dashboard')}
                                </Button>
                            )}
                        </div>
                    </div>

                    {sharingBoard && (
                        <ShareBoardModal
                            board={sharingBoard}
                            boardLabel={analyticsModules.find((module) => module.value === sharingBoard)?.label ?? sharingBoard}
                            open
                            onOpenChange={(next) => !next && setSharingBoard(null)}
                        />
                    )}

                    {/* Dynamic Analytics Component */}
                    <div className="relative min-w-0 flex-1 overflow-auto">
                        <div className={isNavigating ? 'pointer-events-none opacity-50 transition-opacity' : 'transition-opacity'}>
                            {renderAnalyticsComponent()}
                        </div>
                        {isNavigating && (
                            <div className="absolute inset-0 flex items-start justify-center pt-24" aria-live="polite">
                                <div className="bg-background flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    {t('analytics.crm_dashboard.loading', 'Updating…')}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </AnalyticsDocsProvider>
        </AppLayout>
    );
}
